import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { FormattedMessage, useIntl } from 'react-intl';
import styles from './python-code-panel.css';
import {
    workspaceToPython,
    pythonToWorkspace,
    exportPythonFile
} from '../../lib/pixelblock';

const PythonCodePanel = ({ isOpen, onClose }) => {
    const intl = useIntl();
    const [pythonCode, setPythonCode] = useState('');
    const [copySuccess, setCopySuccess] = useState(false);

    const refreshCode = () => {
        const workspace = window.pixelblockWorkspace;
        if (workspace) {
            try {
                const code = workspaceToPython(workspace);
                setPythonCode(code);
            } catch (err) {
                console.error('Error generating Python code:', err);
            }
        }
    };

    useEffect(() => {
        if (isOpen) {
            refreshCode();
        }
    }, [isOpen]);

    useEffect(() => {
        const handleWorkspaceChange = () => {
            if (isOpen) {
                refreshCode();
            }
        };

        window.addEventListener('pixelblock:workspaceChange', handleWorkspaceChange);
        return () => {
            window.removeEventListener('pixelblock:workspaceChange', handleWorkspaceChange);
        };
    }, [isOpen]);

    const handleCopy = () => {
        if (navigator.clipboard) {
            navigator.clipboard.writeText(pythonCode).then(() => {
                setCopySuccess(true);
                setTimeout(() => setCopySuccess(false), 2000);
            });
        }
    };

    const handleExport = () => {
        const workspace = window.pixelblockWorkspace;
        if (workspace) {
            exportPythonFile(workspace, 'pixelblock_project.py');
        }
    };

    const handleApplyToBlocks = () => {
        const workspace = window.pixelblockWorkspace;
        if (!workspace) return;
        try {
            pythonToWorkspace(pythonCode, workspace);
            alert(intl.formatMessage({
                id: 'gui.pixelblock.convertSuccess',
                defaultMessage: 'Python code converted into blocks successfully!'
            }));
        } catch (e) {
            alert(intl.formatMessage({
                id: 'gui.pixelblock.convertError',
                defaultMessage: 'Error converting to blocks: '
            }) + e.message);
        }
    };

    const handleKeyDown = e => {
        if (e.key === 'Tab') {
            e.preventDefault();
            const textarea = e.currentTarget;
            const start = textarea.selectionStart;
            const end = textarea.selectionEnd;
            const value = textarea.value;

            if (e.shiftKey) {
                // Shift + Tab: unindent lines
                const lineStart = value.lastIndexOf('\n', start - 1) + 1;
                const lines = value.substring(lineStart, end).split('\n');
                let removedTotal = 0;
                let firstLineRemoved = 0;
                const unindented = lines.map((line, idx) => {
                    let count = 0;
                    while (count < 4 && line[count] === ' ') {
                        count++;
                    }
                    if (idx === 0) firstLineRemoved = count;
                    removedTotal += count;
                    return line.substring(count);
                }).join('\n');
                const newCode = value.substring(0, lineStart) + unindented + value.substring(end);
                setPythonCode(newCode);
                requestAnimationFrame(() => {
                    textarea.selectionStart = Math.max(lineStart, start - firstLineRemoved);
                    textarea.selectionEnd = Math.max(lineStart, end - removedTotal);
                });
            } else if (start !== end && value.substring(start, end).includes('\n')) {
                // Multi-line selection: indent all selected lines
                const lineStart = value.lastIndexOf('\n', start - 1) + 1;
                const lines = value.substring(lineStart, end).split('\n');
                const indented = lines.map(line => '    ' + line).join('\n');
                const newCode = value.substring(0, lineStart) + indented + value.substring(end);
                setPythonCode(newCode);
                requestAnimationFrame(() => {
                    textarea.selectionStart = start + 4;
                    textarea.selectionEnd = end + (lines.length * 4);
                });
            } else {
                // Single line or simple cursor: insert 4 spaces
                const newCode = value.substring(0, start) + '    ' + value.substring(end);
                setPythonCode(newCode);
                requestAnimationFrame(() => {
                    textarea.selectionStart = textarea.selectionEnd = start + 4;
                });
            }
        }
    };

    if (!isOpen) return null;

    return (
        <div className={styles.panelContainer}>
            <div className={styles.panelHeader}>
                <div className={styles.panelTitle}>
                    <span className={styles.codeIcon}>&lt;/&gt;</span>
                    <FormattedMessage
                        id="gui.pixelblock.pythonCode"
                        defaultMessage="Python Code"
                    />
                </div>
                <div className={styles.headerActions}>
                    <button
                        type="button"
                        className={classNames(styles.actionBtn, styles.actionBtnPrimary)}
                        onClick={handleApplyToBlocks}
                        title={intl.formatMessage({
                            id: 'gui.pixelblock.toBlocksTitle',
                            defaultMessage: 'Parse code into blocks'
                        })}
                    >
                        ⟳ <FormattedMessage id="gui.pixelblock.toBlocks" defaultMessage="To Blocks" />
                    </button>
                    <button
                        type="button"
                        className={styles.actionBtn}
                        onClick={handleCopy}
                        title={intl.formatMessage({
                            id: 'gui.pixelblock.copy',
                            defaultMessage: 'Copy'
                        })}
                    >
                        {copySuccess
                            ? `✓ ${intl.formatMessage({ id: 'gui.pixelblock.copied', defaultMessage: 'Copied' })}`
                            : `📋 ${intl.formatMessage({ id: 'gui.pixelblock.copy', defaultMessage: 'Copy' })}`}
                    </button>
                    <button
                        type="button"
                        className={styles.actionBtn}
                        onClick={handleExport}
                        title={intl.formatMessage({
                            id: 'gui.pixelblock.export',
                            defaultMessage: 'Export'
                        })}
                    >
                        ⬇ <FormattedMessage id="gui.pixelblock.export" defaultMessage="Export" />
                    </button>
                    <button
                        type="button"
                        className={styles.closeButton}
                        onClick={onClose}
                        title={intl.formatMessage({
                            id: 'gui.pixelblock.close',
                            defaultMessage: 'Close'
                        })}
                    >
                        &times;
                    </button>
                </div>
            </div>

            <div className={styles.panelBody}>
                <textarea
                    className={styles.codeArea}
                    value={pythonCode}
                    onChange={e => setPythonCode(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder={intl.formatMessage({
                        id: 'gui.pixelblock.codePlaceholder',
                        defaultMessage: '# Python code will appear here as you add blocks...'
                    })}
                    spellCheck={false}
                />
                <div className={styles.footerBar}>
                    <span className={styles.codeStatus}>
                        <FormattedMessage
                            id="gui.pixelblock.realtimeCode"
                            defaultMessage="Real-time Python code generation"
                        />
                    </span>
                    <button
                        type="button"
                        className={styles.actionBtn}
                        onClick={refreshCode}
                        title={intl.formatMessage({
                            id: 'gui.pixelblock.refresh',
                            defaultMessage: 'Refresh'
                        })}
                    >
                        ↻ <FormattedMessage id="gui.pixelblock.refresh" defaultMessage="Refresh" />
                    </button>
                </div>
            </div>
        </div>
    );
};

PythonCodePanel.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired
};

export default PythonCodePanel;
